import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "../../../hooks/useAuth";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { useState } from "react";
import { ThemeToggle } from "../../ui/theme-toggle";

const loginSchema = z.object({
  email: z.string().email("กรุณากรอกอีเมลที่ถูกต้อง"),
  password: z.string().min(6, "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginForm() {
  const { loginAsync, isLoginLoading } = useAuth();
  const [error, setError] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError("");
      // Clear any cached error counts
      sessionStorage.removeItem("network-error-count");
      localStorage.removeItem("leave-system-auth");

      await loginAsync(data);
      // Redirect to MainLeave page (root path)
      window.location.replace("/");
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        "เข้าสู่ระบบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
      setError(errorMessage);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>เข้าสู่ระบบ</CardTitle>
          <ThemeToggle />
        </div>
        <CardDescription>
          ระบบจัดการการลางาน (Leave Management System)
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          {error && (
            <div
              className={`p-3 text-sm rounded-md ${
                error.includes("pending approval")
                  ? "text-blue-700 bg-blue-50 border border-blue-200"
                  : "text-red-500 bg-red-50 border border-red-200"
              }`}
            >
              {error.includes("pending approval") ? (
                <div className="flex items-start gap-2">
                  <svg
                    className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="font-semibold mb-1">
                      ⏳ บัญชีของคุณอยู่ระหว่างรอการอนุมัติ
                    </p>
                    <p className="text-xs">
                      กรุณารอให้ผู้ดูแลระบบอนุมัติบัญชีของคุณก่อนเข้าใช้งาน
                    </p>
                  </div>
                </div>
              ) : (
                error
              )}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium leading-none">
              อีเมล
            </label>
            <Input
              id="email"
              type="email"
              placeholder="your.email@company.com"
              {...register("email")}
              disabled={isLoginLoading}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="password"
              className="text-sm font-medium leading-none"
            >
              รหัสผ่าน
            </label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              {...register("password")}
              disabled={isLoginLoading}
            />
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-600">จดจำการเข้าสู่ระบบ</span>
            </label>
            <a
              href="/forgot-password"
              className="text-sm text-primary hover:underline"
            >
              ลืมรหัสผ่าน?
            </a>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col space-y-4">
          <Button type="submit" className="w-full" disabled={isLoginLoading}>
            {isLoginLoading ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ"}
          </Button>

          <p className="text-sm text-center text-gray-600">
            ยังไม่มีบัญชี?{" "}
            <a href="/register" className="text-primary hover:underline">
              สมัครสมาชิก
            </a>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}
